import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import './AdvancedAnalytics.css';

const ANALYTICS_CACHE_TTL_MS = 2 * 60 * 1000;

const getCacheKey = (timeRange) => `admin_analytics_${timeRange}`;

const readAnalyticsCache = (timeRange) => {
  try {
    const raw = sessionStorage.getItem(getCacheKey(timeRange));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.createdAt || !parsed?.data) return null;

    if (Date.now() - parsed.createdAt > ANALYTICS_CACHE_TTL_MS) {
      sessionStorage.removeItem(getCacheKey(timeRange));
      return null;
    }

    return parsed.data;
  } catch {
    return null;
  }
};

const writeAnalyticsCache = (timeRange, data) => {
  try {
    sessionStorage.setItem(
      getCacheKey(timeRange),
      JSON.stringify({
        createdAt: Date.now(),
        data
      })
    );
  } catch {
    // Ignore storage quota and serialization failures.
  }
};

const AdvancedAnalytics = () => {
  const [analytics, setAnalytics] = useState({
    pageViews: [],
    topPages: [],
    deviceStats: {},
    browserStats: {},
    referrerStats: {},
    hourlyStats: {},
    realTimeVisitors: 0,
    sessionData: {},
    conversionMetrics: {}
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState('7days');

  useEffect(() => {
    fetchAdvancedAnalytics();
  }, [selectedTimeRange]);

  const fetchAdvancedAnalytics = async () => {
    try {
      const cachedData = readAnalyticsCache(selectedTimeRange);
      if (cachedData) {
        setAnalytics(cachedData);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      // Run both reads in parallel to reduce perceived loading time.
      const [pageViewsData, sessionsData] = await Promise.all([
        fetchPageViews(),
        fetchUserSessions()
      ]);
      
      // Process analytics data
      const processedData = {
        pageViews: pageViewsData,
        topPages: getTopPages(pageViewsData),
        deviceStats: getDeviceStats(pageViewsData),
        browserStats: getBrowserStats(pageViewsData),
        referrerStats: getReferrerStats(pageViewsData),
        hourlyStats: getHourlyStats(pageViewsData),
        realTimeVisitors: getRealTimeVisitors(sessionsData),
        sessionData: processSessionData(sessionsData),
        conversionMetrics: calculateConversionMetrics(pageViewsData)
      };

      setAnalytics(processedData);
      writeAnalyticsCache(selectedTimeRange, processedData);
    } catch (err) {
      setError('Error fetching advanced analytics: ' + err.message);
      console.error('Error fetching advanced analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPageViews = async () => {
    try {
      const pageViewsRef = collection(db, 'analytics', 'page_views', 'visits');
      const q = query(pageViewsRef, orderBy('timestamp', 'desc'), limit(1000));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(view => isWithinTimeRange(view.timestamp));
    } catch (error) {
      console.warn('Page views collection not found, returning empty array');
      return [];
    }
  };

  const fetchUserSessions = async () => {
    try {
      const sessionsRef = collection(db, 'analytics', 'user_sessions', 'sessions');
      const q = query(sessionsRef, orderBy('startTime', 'desc'), limit(500));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(session => isWithinTimeRange(session.startTime));
    } catch (error) {
      console.warn('User sessions collection not found, returning empty array');
      return [];
    }
  };

  const isWithinTimeRange = (timestamp) => {
    if (!timestamp) return false;
    
    const date = new Date(timestamp);
    const now = new Date();
    const daysDiff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    switch (selectedTimeRange) {
      case '1day': return daysDiff === 0;
      case '7days': return daysDiff <= 7;
      case '30days': return daysDiff <= 30;
      case '90days': return daysDiff <= 90;
      default: return daysDiff <= 7;
    }
  };

  const getTopPages = (pageViews) => {
    const pageCounts = {};
    pageViews.forEach(view => {
      const path = view.path || '/';
      pageCounts[path] = (pageCounts[path] || 0) + 1;
    });
    
    return Object.entries(pageCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([path, count]) => ({ path, count }));
  };

  const getDeviceStats = (pageViews) => {
    const devices = { mobile: 0, desktop: 0, tablet: 0, unknown: 0 };
    
    pageViews.forEach(view => {
      const userAgent = view.userAgent || '';
      if (/Mobile|Android|iPhone|iPad/.test(userAgent)) {
        if (/iPad/.test(userAgent)) {
          devices.tablet++;
        } else {
          devices.mobile++;
        }
      } else if (/Windows|Mac|Linux/.test(userAgent)) {
        devices.desktop++;
      } else {
        devices.unknown++;
      }
    });
    
    return devices;
  };

  const getBrowserStats = (pageViews) => {
    const browsers = {};
    
    pageViews.forEach(view => {
      const userAgent = view.userAgent || '';
      let browser = 'Unknown';
      
      if (userAgent.includes('Chrome')) browser = 'Chrome';
      else if (userAgent.includes('Firefox')) browser = 'Firefox';
      else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browser = 'Safari';
      else if (userAgent.includes('Edge')) browser = 'Edge';
      else if (userAgent.includes('Opera')) browser = 'Opera';
      
      browsers[browser] = (browsers[browser] || 0) + 1;
    });
    
    return browsers;
  };

  const getReferrerStats = (pageViews) => {
    const referrers = {};
    
    pageViews.forEach(view => {
      let referrer = view.referrer || 'Direct';
      
      if (referrer === '') referrer = 'Direct';
      else if (referrer.includes('google')) referrer = 'Google';
      else if (referrer.includes('facebook')) referrer = 'Facebook';
      else if (referrer.includes('twitter')) referrer = 'Twitter';
      else if (referrer.includes('linkedin')) referrer = 'LinkedIn';
      else if (referrer !== 'Direct') referrer = 'Other';
      
      referrers[referrer] = (referrers[referrer] || 0) + 1;
    });
    
    return referrers;
  };

  const getHourlyStats = (pageViews) => {
    const hourly = {};
    for (let i = 0; i < 24; i++) {
      hourly[i] = 0;
    }
    
    pageViews.forEach(view => {
      if (view.timestamp) {
        const hour = new Date(view.timestamp).getHours();
        hourly[hour] = (hourly[hour] || 0) + 1;
      }
    });
    
    return hourly;
  };

  const getRealTimeVisitors = (sessions) => {
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return sessions.filter(session => 
      session.lastActivity && new Date(session.lastActivity) > fiveMinutesAgo
    ).length;
  };

  const processSessionData = (sessions) => {
    const totalSessions = sessions.length;
    const totalDuration = sessions.reduce((sum, session) => {
      return sum + (session.duration || 0);
    }, 0);
    
    const avgSessionDuration = totalSessions > 0 ? totalDuration / totalSessions : 0;
    const bounceRate = sessions.filter(session => session.pageViews === 1).length / totalSessions * 100;
    
    return {
      totalSessions,
      avgSessionDuration: Math.round(avgSessionDuration / 1000), // Convert to seconds
      bounceRate: Math.round(bounceRate)
    };
  };

  const calculateConversionMetrics = (pageViews) => {
    const totalViews = pageViews.length;
    const cartViews = pageViews.filter(view => view.path?.includes('/cart')).length;
    const checkoutViews = pageViews.filter(view => view.path?.includes('/checkout')).length;
    const productViews = pageViews.filter(view => view.path?.includes('/product')).length;
    
    return {
      cartConversion: totalViews > 0 ? ((cartViews / totalViews) * 100).toFixed(2) : 0,
      checkoutConversion: totalViews > 0 ? ((checkoutViews / totalViews) * 100).toFixed(2) : 0,
      productEngagement: totalViews > 0 ? ((productViews / totalViews) * 100).toFixed(2) : 0
    };
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatNumber = (value) => Number(value || 0).toLocaleString('en-IN');
  const safeMax = (values) => Math.max(1, ...values);

  if (loading) {
    return <div className="advanced-analytics-loading">Loading advanced analytics...</div>;
  }

  if (error) {
    return <div className="advanced-analytics-error">{error}</div>;
  }

  const maxDeviceCount = safeMax(Object.values(analytics.deviceStats || {}));
  const maxBrowserCount = safeMax(Object.values(analytics.browserStats || {}));
  const maxReferrerCount = safeMax(Object.values(analytics.referrerStats || {}));
  const maxHourlyCount = safeMax(Object.values(analytics.hourlyStats || {}));

  return (
    <div className="advanced-analytics">
      <div className="analytics-header">
        <h2>📈 Advanced Analytics</h2>
        <div className="time-range-selector">
          <select 
            value={selectedTimeRange} 
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="time-range-select"
          >
            <option value="1day">Last 24 Hours</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
          </select>
        </div>
      </div>

      {/* Real-time & Session Metrics */}
      <div className="metrics-grid">
        <div className="metric-card real-time">
          <div className="metric-icon">🔴</div>
          <div className="metric-content">
            <h3>Real-time Visitors</h3>
            <div className="metric-value">{formatNumber(analytics.realTimeVisitors)}</div>
            <span className="metric-label">Active now</span>
          </div>
        </div>
        
        <div className="metric-card sessions">
          <div className="metric-icon">📊</div>
          <div className="metric-content">
            <h3>Total Sessions</h3>
            <div className="metric-value">{formatNumber(analytics.sessionData.totalSessions || 0)}</div>
            <span className="metric-label">In selected period</span>
          </div>
        </div>
        
        <div className="metric-card duration">
          <div className="metric-icon">⏱️</div>
          <div className="metric-content">
            <h3>Avg Session Duration</h3>
            <div className="metric-value">{formatNumber(analytics.sessionData.avgSessionDuration || 0)}s</div>
            <span className="metric-label">Average time</span>
          </div>
        </div>
        
        <div className="metric-card bounce">
          <div className="metric-icon">↩️</div>
          <div className="metric-content">
            <h3>Bounce Rate</h3>
            <div className="metric-value">{formatNumber(analytics.sessionData.bounceRate || 0)}%</div>
            <span className="metric-label">Single page visits</span>
          </div>
        </div>
      </div>

      {/* Conversion Metrics */}
      <div className="conversion-section">
        <h3>🎯 Conversion Metrics</h3>
        <div className="conversion-grid">
          <div className="conversion-card">
            <h4>Product Engagement</h4>
            <div className="conversion-value">{analytics.conversionMetrics.productEngagement}%</div>
            <span>Product page views</span>
          </div>
          <div className="conversion-card">
            <h4>Cart Conversion</h4>
            <div className="conversion-value">{analytics.conversionMetrics.cartConversion}%</div>
            <span>Visitors who added to cart</span>
          </div>
          <div className="conversion-card">
            <h4>Checkout Rate</h4>
            <div className="conversion-value">{analytics.conversionMetrics.checkoutConversion}%</div>
            <span>Visitors who reached checkout</span>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        {/* Top Pages */}
        <div className="chart-card">
          <h3>📄 Top Pages</h3>
          <div className="top-pages-list">
            {analytics.topPages.slice(0, 5).map((page, index) => (
              <div key={page.path} className="page-item">
                <span className="page-rank">#{index + 1}</span>
                <span className="page-path">{page.path}</span>
                <span className="page-count">{formatNumber(page.count)} views</span>
              </div>
            ))}
          </div>
        </div>

        {/* Device Stats */}
        <div className="chart-card">
          <h3>📱 Device Breakdown</h3>
          <div className="device-stats">
            {Object.entries(analytics.deviceStats).map(([device, count]) => (
              <div key={device} className="device-item">
                <span className="device-name">{device.charAt(0).toUpperCase() + device.slice(1)}</span>
                <div className="device-bar">
                  <div 
                    className="device-fill" 
                    style={{
                        width: `${(count / maxDeviceCount) * 100}%`
                    }}
                  ></div>
                </div>
                  <span className="device-count">{formatNumber(count)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Browser Stats */}
        <div className="chart-card">
          <h3>🌐 Browser Usage</h3>
          <div className="browser-stats">
            {Object.entries(analytics.browserStats).slice(0, 5).map(([browser, count]) => (
              <div key={browser} className="browser-item">
                <span className="browser-name">{browser}</span>
                <div className="browser-bar">
                  <div 
                    className="browser-fill" 
                    style={{
                        width: `${(count / maxBrowserCount) * 100}%`
                    }}
                  ></div>
                </div>
                  <span className="browser-count">{formatNumber(count)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Traffic Sources */}
        <div className="chart-card">
          <h3>🔗 Traffic Sources</h3>
          <div className="referrer-stats">
            {Object.entries(analytics.referrerStats).map(([source, count]) => (
              <div key={source} className="referrer-item">
                <span className="referrer-name">{source}</span>
                <div className="referrer-bar">
                  <div 
                    className="referrer-fill" 
                    style={{
                        width: `${(count / maxReferrerCount) * 100}%`
                    }}
                  ></div>
                </div>
                  <span className="referrer-count">{formatNumber(count)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Hourly Activity */}
      <div className="hourly-activity">
        <h3>🕐 Hourly Activity</h3>
        <div className="hourly-chart">
          {Object.entries(analytics.hourlyStats).map(([hour, count]) => (
            <div key={hour} className="hour-bar">
              <div 
                className="hour-fill"
                style={{
                  height: `${Math.max(10, (count / maxHourlyCount) * 100)}px`
                }}
                title={`${hour}:00 - ${formatNumber(count)} visits`}
              ></div>
              <span className="hour-label">{hour}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="analytics-actions">
        <button onClick={fetchAdvancedAnalytics} className="refresh-analytics-btn">
          🔄 Refresh Analytics
        </button>
        <button 
          onClick={() => {
            const data = JSON.stringify(analytics, null, 2);
            const blob = new Blob([data], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `analytics-${new Date().toISOString().split('T')[0]}.json`;
            a.click();
            URL.revokeObjectURL(url);
          }}
          className="export-btn"
        >
          📥 Export Data
        </button>
      </div>
    </div>
  );
};

export default AdvancedAnalytics;